import { isPlatformBrowser } from '@angular/common';
import { DestroyRef, inject, Injectable, PLATFORM_ID } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, firstValueFrom } from 'rxjs';
import { AuthService } from '@/core/services/auth/auth.service';
import { WebSocketService } from '@/core/services/websocket.service';
import { AppDeploymentService } from '@/shared/openapi/api/app-deployment.service';
import { TranslationsReloadService } from './translations-reload.service';

/** app_deployment target the Translations repo bumps on every publish. */
export const TRANSLATIONS_DEPLOYMENT_TARGET = 3;

/** Response shape from app-deployment getByApplication (application=3). */
interface TranslationsDeploymentResponse {
  translationsVersion?: string;
}

/**
 * Hot-reloads translations in open tabs when the Translations repo publishes:
 * the socket event, plus a catch-up check on reconnect and focus.
 */
@Injectable({ providedIn: 'root' })
export class TranslationsUpdateService {
  private readonly appDeploymentService = inject(AppDeploymentService);
  private readonly webSocket = inject(WebSocketService);
  private readonly authService = inject(AuthService);
  private readonly translationsReload = inject(TranslationsReloadService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly destroyRef = inject(DestroyRef);

  /** Version whose strings this tab shows. Set by the first authenticated check. */
  private sessionVersion: string | null = null;
  private reloading = false;
  private watching = false;

  /** Call once from the app shell. */
  watch(): void {
    if (this.watching || !isPlatformBrowser(this.platformId)) {
      return;
    }
    this.watching = true;

    this.webSocket
      .appVersionChanged()
      .pipe(
        filter(
          message =>
            Number(message.application) === TRANSLATIONS_DEPLOYMENT_TARGET
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(message => void this.applyRemoteVersion(message.version));

    this.webSocket
      .socketConnected()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => void this.checkForUpdate());

    const abort = new AbortController();
    this.destroyRef.onDestroy(() => abort.abort());
    window.addEventListener('focus', this.onFocus, { signal: abort.signal });

    // A session restored at boot is active before watch(); take the baseline now.
    if (this.authService.hasActiveSession) {
      void this.checkForUpdate();
    }
  }

  // Gated on the session, not the socket, so a tab whose socket is down still catches up.
  private onFocus = (): void => {
    if (!this.authService.hasActiveSession) {
      return;
    }
    void this.checkForUpdate();
  };

  private async checkForUpdate(): Promise<void> {
    let response: TranslationsDeploymentResponse;
    try {
      response = (await firstValueFrom(
        this.appDeploymentService.appDeploymentControllerGetByApplication({
          application: TRANSLATIONS_DEPLOYMENT_TARGET,
        })
      )) as TranslationsDeploymentResponse;
    } catch (error) {
      console.error('Failed to check translations version:', error);
      return;
    }
    await this.applyRemoteVersion(response.translationsVersion);
  }

  private async applyRemoteVersion(version: string | undefined): Promise<void> {
    if (!version) {
      return;
    }
    if (this.sessionVersion === null) {
      this.sessionVersion = version;
      return;
    }
    // A version that lands mid-reload is caught by the next focus or reconnect check.
    if (this.reloading || version === this.sessionVersion) {
      return;
    }

    // sessionVersion stays put on failure, so the next check retries.
    this.reloading = true;
    try {
      await this.translationsReload.reload();
      this.sessionVersion = version;
    } catch (error) {
      console.error('Failed to hot-reload translations:', error);
    } finally {
      this.reloading = false;
    }
  }
}
